import {Item, User} from "../types";
import * as React from "react";
import {useCallback, useMemo} from "react";
import {
    classNames,
    layout,
    MaterialIcon,
    PushButton,
    theme,
    typography,
    useBoxes,
    useOccupancies,
    usePlatform,
    View,
    ViewContent,
    ViewFooter,
    ViewFooterItem,
    ViewHeader,
    ViewHeaderItem
} from "@variocube/cube-ui";
import {useCurrentRental} from "../hooks";
import {BoxControl} from "./BoxControl";
import {RentalDetail} from "./RentalDetail";
import {stylesheet} from "typestyle";
import {ItemRemoval} from "./types";
import {Box, LogLevel, Occupancy} from "@variocube/app-api";
import {useLocalization} from "../../i18n";

export interface ItemViewProps {
    user?: User;
    item: Item;
    onBack: () => void;
    onStoreItem: (item: Item) => void;
    onRemoveItem: (removal: ItemRemoval) => void;
}

export function ItemView({user, item, onBack, onStoreItem, onRemoveItem}: ItemViewProps) {
    const {t} = useLocalization();
    const platform = usePlatform();
    const occupancies = useOccupancies();
    const occupancy = useMemo<Occupancy | undefined>(() => occupancies.find(occupancy => occupancy.content.item == item.uuid), [occupancies]);
    const boxes = useBoxes();
    const box = useMemo<Box | undefined>(() => boxes.find(box => box.number == occupancy?.boxNumber), [boxes, occupancy]);

    const handleStore = useCallback(() => onStoreItem(item), [onStoreItem, item]);
    const handleRemove = useCallback(() => occupancy && box && onRemoveItem({item, occupancy, box}), [onRemoveItem, item, occupancy, box]);
    const handleOpen = useCallback(async (boxNumber: string) => {
        await platform.openBoxLock(boxNumber);
        await platform.log(LogLevel.Trace, "Open box command", {...user, box: boxNumber});
    }, [platform, user]);

    const rental = useCurrentRental(item.uuid);

    return (
        <View>
            <ViewHeader>
                <ViewHeaderItem fill className={layout.paddingMedium}>
                    <div className={typography.title}>{item.name}</div>
                    <div className={typography.description}>{item.description}</div>
                    <div className={typography.description}>{item.foreignId}</div>
                </ViewHeaderItem>
            </ViewHeader>
            <ViewContent>
                <div className={classNames(layout.horizontal, styles.content)}>
                    <div className={classNames(layout.fill, theme.moreContrast, layout.paddingMedium, styles.halfWidth)}>
                        <div className={typography.title}>{t("Box")}</div>
                        {occupancy && (
                            <div>
                                <BoxControl boxNumber={occupancy.boxNumber} onBoxOpened={() => handleOpen(occupancy.boxNumber)}/>
                            </div>
                        )}
                        {!occupancy && (
                            <div>{t("ItemNotStored")}</div>
                        )}
                    </div>
                    <RentalDetail
                        rental={rental} item={item}
                        className={classNames(layout.fill, theme.lessContrast, layout.paddingMedium, styles.halfWidth)}
                    />
                </div>
            </ViewContent>
            <ViewFooter className={classNames(layout.paddingMedium, layout.spaceBetween)}>
                <ViewFooterItem>
                    <PushButton onClick={onBack} icon={MaterialIcon.arrow_back}>
                        {t("Back")}
                    </PushButton>
                </ViewFooterItem>
                <ViewFooterItem>
                    {occupancy && (
                        <PushButton onClick={handleRemove} primary>
                            {t("RemoveItem")}
                        </PushButton>
                    )}
                    {!occupancy && (
                        <PushButton onClick={handleStore} primary>
                            {t("StoreItem")}
                        </PushButton>
                    )}
                </ViewFooterItem>
            </ViewFooter>
        </View>
    )
}

const styles = stylesheet({
    halfWidth: {
        width: "50%"
    },
    content: {
        minHeight: "100%"
    }
});